import { n, t } from '@/ui/i18n/i18n';
import { NumberFormatter } from '@/ui/i18n/numberFormats';
import { tag } from '@/ui/components';
import { h } from 'vue';

export const useSensorFormat = () => {
  const withinBounds = (
    measurement: number,
    lowerBound: number | null,
    upperBound: number | null,
  ) => {
    if (lowerBound === null && upperBound === null) {
      return true;
    }

    if (lowerBound === null && upperBound !== null) {
      return measurement <= upperBound;
    }

    if (upperBound === null && lowerBound !== null) {
      return measurement >= lowerBound;
    }

    return measurement >= (lowerBound as unknown as number) && measurement <= (upperBound as unknown as number);
  };

  const hasBounds = (
    lowerBound: number | null,
    upperBound: number | null,
  ) => lowerBound !== null || upperBound !== null;

  const formatBounds = (
    lowerBound: number | null,
    upperBound: number | null,
  ): string => {
    if (lowerBound !== null && upperBound !== null) {
      return `${n(lowerBound, 'metricTemperature')} - ${n(upperBound, 'metricTemperature')}`;
    }
    if (lowerBound !== null) {
      return t('sensors.min-value', { value: n(lowerBound, 'metricTemperature') });
    }
    if (upperBound !== null) {
      return t('sensors.max-value', { value: n(upperBound, 'metricTemperature') });
    }
    return '';
  };

  const formatMetric = (
    measurement: number | null,
    lowerBound: number | null,
    upperBound: number | null,
    formatter: NumberFormatter,
  ) => {
    if (measurement === null) {
      return null;
    }

    const formattedValue = n(measurement, formatter);
    // The sensor measurements coming from the backend is converted
    // from percentage to a decimal base. This means that if we have
    // a humidity of 50% we will receive 0.5. The upperbound and
    // lowerBound measurement are in decimal base. That's
    // why we need to make this normalization
    const measurementNormalized = formatter === 'metricHumidity' ? measurement * 100 : measurement;

    if (hasBounds(lowerBound, upperBound)) {
      return [
        tag(
          withinBounds(measurementNormalized, lowerBound, upperBound)
            ? 'success'
            : 'error',
          formattedValue,
        ),
        h('div', { class: 'is-size-7 has-text-grey' }, formatBounds(lowerBound, upperBound)),
      ];
    }

    return tag('neutral', formattedValue);
  };

  return { formatMetric };
};
